import { MapPin, Phone, Mail, MessageCircle, Clock, Navigation } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { CONTACT } from "@/lib/constants";

export default function Contact() {
  return (
    <>
      <SEO title="Contact Us" description="Get in touch with Forage Animal Health & Nutrition. Our team responds to enquiries within 24 hours." path="/contact" />

      <section className="bg-[#0B2545] text-white py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-fr relative">
          <span className="overline text-[#2A9D8F]">Contact Us</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 leading-tight max-w-3xl">
            Let's start a conversation.
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-2xl">
            Distributor enquiry, contract manufacturing, technical support or
            export documentation — we respond personally within 24 hours.
          </p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-fr grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1 space-y-6">
            <div className="p-6 rounded-xl bg-[#F8FAFC] border border-slate-200">
              <MapPin className="text-[#2A9D8F]" size={22} />
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-3">Head Office</div>
              <div className="font-display text-lg font-bold text-[#0B2545] mt-1">{CONTACT.company}</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                {CONTACT.address.line1}<br />
                {CONTACT.address.line2}, {CONTACT.address.state}<br />
                {CONTACT.address.pincode}, {CONTACT.address.country}
              </p>
            </div>
            <div className="p-6 rounded-xl bg-[#F8FAFC] border border-slate-200">
              <Phone className="text-[#2A9D8F]" size={22} />
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-3">Phone</div>
              <a href={`tel:${CONTACT.phoneTel}`} className="block font-display text-lg font-bold text-[#0B2545] mt-1 hover:text-[#2A9D8F]" data-testid="contact-phone">
                {CONTACT.phone}
              </a>
            </div>
            <div className="p-6 rounded-xl bg-[#F8FAFC] border border-slate-200">
              <Mail className="text-[#2A9D8F]" size={22} />
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-3">Email</div>
              <a href={`mailto:${CONTACT.email}`} className="block text-base font-semibold text-[#0B2545] mt-1 hover:text-[#2A9D8F]" data-testid="contact-email-link">{CONTACT.email}</a>
              <a href={`mailto:${CONTACT.enquiriesEmail}`} className="block text-sm text-slate-600 mt-1 hover:text-[#2A9D8F]">{CONTACT.enquiriesEmail}</a>
            </div>
            <div className="p-6 rounded-xl bg-[#0B2545] text-white">
              <MessageCircle className="text-[#2A9D8F]" size={22} />
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-3">WhatsApp</div>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="block font-display text-lg font-bold mt-1 hover:text-[#2A9D8F]" data-testid="contact-whatsapp">
                +{CONTACT.whatsapp}
              </a>
              <div className="text-xs text-slate-400 mt-2 flex items-center gap-2">
                <Clock size={12} /> Mon–Sat • 9:30 AM – 7:00 PM IST
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="font-display text-2xl font-bold text-[#0B2545]">Send us a message</h3>
            <p className="text-sm text-slate-500 mt-1 mb-6">All fields marked * are required.</p>
            <ContactForm testIdPrefix="contact-page" />
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-[#F8FAFC] pb-20 px-6 md:px-12 lg:px-24">
        <div className="container-fr">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
            <div>
              <span className="overline">Find Us</span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0B2545] mt-2">
                {CONTACT.company}
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                {CONTACT.address.line1}, {CONTACT.address.line2},{" "}
                {CONTACT.address.state} - {CONTACT.address.pincode}
              </p>
            </div>
            <a
              href={CONTACT.mapsLink}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-directions"
              className="btn-primary inline-flex items-center gap-2 self-start md:self-auto"
            >
              <Navigation size={16} /> Get Directions
            </a>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-md bg-white">
            <iframe
              title={`${CONTACT.company} location on Google Maps`}
              src={CONTACT.mapsEmbed}
              width="100%"
              height="480"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>
      </section>
    </>
  );
}
